import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as dayjs from 'dayjs';
import PropTypes from 'prop-types';

import GatsbyImage from 'gatsby-image';
import { NetlifyForm, Input, TextArea, Select } from './form-elements';
import { Error } from './form-elements/error';

const flavours = [
  'Chocolate (GF)',
  'Passionfruit (GF)',
  'Vanilla Bean (GF)',
  'Coffee (GF)',
  'Cookies and Cream',
  'Mint Choc Chip (GF)',
  'Snickers (GF)',
  'Ferrero',
  'Burnt Caramel (GF)',
  'Bubblegum',
  'Hubba Bubba (GF)',
  'Lemon Sorbet (GF DF V)',
  'Strawberry Sorbet (GF DF V)',
  'Mango Sorbet (GF DF V)',
  'Black Hawaii – charcoal coconut & chocolate (GF DF V)',
  'Salted Coconut Pea Flower (GF DF V)',
];

function RequestForm({ cakes }) {
  const { register, handleSubmit, errors } = useForm({ mode: 'onBlur' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  return (
    <article className="relative py-4 bg-gold">
      <div className="relative px-2 py-8 lg:px-8">
        <div className="relative w-full max-w-2xl mx-auto bg-white rounded-lg">
          <div className="px-4 py-12 sm:px-6 lg:px-8">
            <h2 className="pb-8 text-3xl font-black leading-none tracking-wide text-center uppercase text-slate font-display">
              Cakes online form
            </h2>
            <NetlifyForm
              handleSubmit={handleSubmit}
              register={register}
              setIsSubmitting={setIsSubmitting}
              action="/success/"
              name="request_form"
              className="w-full max-w-lg mx-auto space-y-4"
            >
              <Input
                name="first_name"
                label="First name"
                register={register}
                errors={errors}
              />
              <Input
                name="last_name"
                label="Last name"
                register={register}
                errors={errors}
              />
              <Input
                name="email"
                label="Email address"
                type="email"
                register={register}
                errors={errors}
              />
              <Input
                name="contact_number"
                label="Contact number"
                type="tel"
                register={register}
                errors={errors}
              />
              <Input
                name="pickup_date"
                min={dayjs().add(3, 'day').format('YYYY-MM-DD')}
                type="date"
                label="Date of pick up (please allow at least 3 days notice)"
                register={register}
                errors={errors}
              />
              <Input
                name="pickup_time"
                type="time"
                min="10:00"
                max="21:00"
                step={60}
                label="Approximate time of pick up"
                register={register}
                errors={errors}
              />
              <Input
                name="allergies_dietary"
                label="Allergies / dietary requirements"
                register={register}
                errors={errors}
                required={false}
              />
              <Select
                name="size"
                label="Size"
                defaultValue="Size"
                options={[
                  'Small - 850mL - 8 people - $45',
                  'Medium - 1.9L - 16 people - $70',
                  'Large Tiered - 2.75L - 24 people - $90',
                ]}
                register={register}
                errors={errors}
              />
              <Select
                name="flavour_1"
                label="Flavour 1"
                options={flavours}
                register={register}
                errors={errors}
              />
              <Select
                name="flavour_2"
                label="Flavour 2"
                options={flavours}
                register={register}
                errors={errors}
              />
              <DecorationRadioButtons
                legend="Decoration ideas, or customise your own"
                name="decorations"
                cakes={cakes}
                register={register}
                errors={errors}
              />
              <TextArea
                name="inquiry"
                label="Custom Order Inquiry (Optional)"
                register={register}
                errors={errors}
                required={false}
              />
              <div className="py-8 text-sm break-words">
                {/* <p>
                  Any flavour outside of our standard range will incur a $9.00
                  fee per flavour.
                </p> */}
                <p>Prices may vary depending on request.</p>
                <p>Bringing a cool bag is recommended for pick up.</p>
                <p>Cakes to be stored in freezer.</p>
              </div>
              <div className="flex justify-between">
                <p>* Required field</p>
                <span className="inline-flex">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`inline-flex w-full px-6 py-1 text-xl font-black leading-6 tracking-wide text-white uppercase transition duration-150 ease-in-out bg-gray-800 border border-transparent rounded-lg font-display hover:bg-gray-700 focus:border-gray-900 active:bg-gray-900 ${
                      isSubmitting ? 'opacity-50 cursor-wait' : ''
                    }`}
                  >
                    Submit
                  </button>
                </span>
              </div>
            </NetlifyForm>
          </div>
        </div>
        <div className="relative w-full max-w-2xl mx-auto mt-12 prose">
          <p>
            If you require a cake last minute , phone our shop on{' '}
            <a href="tel:0491060099">0491060099</a> and one of our friendly
            staff members can assist you.
          </p>
          <h2>HOW TO LOOK AFTER YOUR GELATO CAKE/TAKING YOUR CAKE HOME</h2>
          <ul>
            <li>All of our cakes need to be stored in the freezer.</li>
            <li>
              We will supply you with a box to transport your cake home and we
              advise keeping your cake inside the box in the freezer to make
              sure no damage is done.
            </li>
            <li>
              Our cakes can be transported in our boxes for around 20 minutes
              before needing refrigeration. If travelling a longer distance
              please make sure you bring a cooler bag.
            </li>
          </ul>

          <h2>SERVING YOUR CAKE</h2>
          <ul>
            <li>
              Take your cake out of the freezer a few minutes before serving so
              that it is not too hard to cut into.
            </li>
            <li>
              For nice, clean slices use a hot knife that has been wiped dry.
            </li>
            <li>
              Remember that gelato cakes are richer than normal cakes and we
              recommended smaller slices that you might usually serve.
            </li>
          </ul>
          <p>
            CREATIVE LICENSE - At Blue Cow Gelato, our cake maker will follow
            customer provided recommendations and specifications and to the best
            of their ability. However creative license is often needed to ensure
            that every product that leaves our premise will reflect our highest
            standards. Every item that leaves our kitchen is handmade, meaning
            each item is unique. Blue Cow Gelato cannot guarantee 100% accuracy
            in regards to decoration specifications.
          </p>
        </div>
      </div>
    </article>
  );
}

RequestForm.propTypes = {
  cakes: PropTypes.array.isRequired,
};

function DecorationRadioButtons({
  cakes,
  errors,
  legend,
  name,
  register,
  required = true,
}) {
  return (
    <fieldset>
      <legend className="">
        {legend} {required && '*'}
      </legend>
      <div className="grid pt-1 mt-px ml-px bg-white rounded-md shadow-sm">
        {cakes.map((cake, index) => (
          <div
            key={cake.id}
            className={`relative flex p-4 -mt-px -ml-px border space-x-4
                      ${
                        index === 0 || index === 1
                          ? 'rounded-tl-md rounded-tr-md'
                          : ''
                      }

                      ${
                        index === cakes.length - 1
                          ? 'rounded-bl-md rounded-br-md'
                          : ''
                      }
                      `}
          >
            <div className="flex items-center h-5">
              <label
                htmlFor={`${name}-${cake.title
                  .split(' ')
                  .join('_')
                  .toLowerCase()}`}
                className="flex flex-col flex-1 ml-3 cursor-pointer"
              >
                <span className="sr-only">Decoration: </span>
                <input
                  id={`${name}-${cake.title
                    .split(' ')
                    .join('_')
                    .toLowerCase()}`}
                  name={name}
                  type="radio"
                  value={cake.title}
                  defaultChecked={false}
                  required={required}
                  ref={register({
                    required: <Error message="This field is required" />,
                  })}
                  className="w-4 h-4 border-gray-300 rounded-full cursor-pointer text-slate focus:ring"
                />
              </label>
            </div>
            {/* <label
              htmlFor={`${name}-${cake.title
                .split(' ')
                .join('_')
                .toLowerCase()}`}
              className="flex flex-col flex-1 ml-3 cursor-pointer"
            > */}
            <div className="flex flex-col justify-between w-full -mt-4 sm:flex-row">
              <span className="block mt-4 text-sm font-medium">
                {cake.title}
              </span>
              <div>
                <GatsbyImage
                  fluid={cake.image.childImageSharp.fluid}
                  className="flex-shrink-0 w-20 h-20 mt-4 rounded-full"
                />
              </div>
            </div>
            {/* </label> */}
          </div>
        ))}
      </div>
      {errors[name]?.message}
    </fieldset>
  );
}

DecorationRadioButtons.propTypes = {
  cakes: PropTypes.array.isRequired,
  errors: PropTypes.object,
  legend: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  required: PropTypes.bool,
};

export { RequestForm };
